/**
 * Flashing Description Admin Dialog (Iter229o)
 * ────────────────────────────────────────────
 * Lightweight pop-out for adding / renaming / removing the canonical
 * Backtray flashing descriptions surfaced in the Roof Diagram
 * Annotator's `Pin.Flashing` dialog.
 *
 * Renders inside an existing pin-edit modal — therefore it's a
 * portal-free `<div>` overlay positioned absolute / fixed so the
 * underlying pin edit form stays mounted (no state loss when the
 * operator hits ✕ to close).
 *
 * Data flow: GET / POST / PATCH / DELETE `/api/flashing-descriptions`.
 * Parent passes in `api` + an `onClose` callback. On any mutation we
 * also call `onChanged()` so the parent select can refetch.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const GROUPS = [
  { value: 'cascading', label: 'Cascading (carries fittings)' },
  { value: 'simple',    label: 'Simple cover (no fittings)' },
];

export default function FlashingDescriptionAdminDialog({ api, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ label: '', group: 'simple' });
  const [edits, setEdits] = useState({}); // id → { label?, group? }
  // Iter232b — Bulk-parse Ø dialog state
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // { scanned, would_change, proposed[] }

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/flashing-descriptions');
      setItems(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      toast.error('Could not load flashing descriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const createItem = async () => {
    const label = (draft.label || '').trim();
    if (!label) {
      toast.error('Label is required');
      return;
    }
    setCreating(true);
    try {
      await api.post('/flashing-descriptions', { label, group: draft.group });
      toast.success(`Added "${label}"`);
      setDraft({ label: '', group: 'simple' });
      await refresh();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Add failed: ${detail}`);
    } finally {
      setCreating(false);
    }
  };

  const patchItem = async (item, patch) => {
    try {
      await api.patch(`/flashing-descriptions/${item._id}`, patch);
      await refresh();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Update failed: ${detail}`);
    }
  };

  const deleteItem = async (item) => {
    const label = item.is_seed ? `seed "${item.label}"` : `"${item.label}"`;
    if (!window.confirm(`Delete ${label}? It will be hidden from the dropdown — pins already using it keep working.`)) return;
    try {
      await api.delete(`/flashing-descriptions/${item._id}`);
      toast.success('Deleted');
      await refresh();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Delete failed: ${detail}`);
    }
  };

  // Iter232b — Bulk-flag parseable Ø. Two-phase UX:
  //   1) Dry-run preview (so the admin sees which rows will change)
  //   2) Apply on confirm.
  const bulkPreviewParseable = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post('/flashing-descriptions/bulk-parse-diameters', { apply: false });
      setBulkPreview(r?.data || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Preview failed: ${detail}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkApplyParseable = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post('/flashing-descriptions/bulk-parse-diameters', { apply: true });
      const n = r?.data?.would_change || 0;
      toast.success(`Bulk-flagged ${n} description${n === 1 ? '' : 's'}`);
      setBulkPreview(null);
      await refresh();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Apply failed: ${detail}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Group items by `group` for a 2-section list.
  const grouped = GROUPS.map(g => ({
    ...g,
    rows: items.filter(i => (i.group || 'simple') === g.value),
  }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60"
      data-testid="flashing-desc-admin-backdrop"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl ring-1 ring-slate-300 w-[560px] max-w-[95vw] max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="flashing-desc-admin-dialog"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Flashing descriptions</h2>
            <p className="text-[10px] text-slate-500 mt-0.5">Admin list — drives the Pin.Flashing description dropdown.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={bulkPreviewParseable}
              disabled={bulkBusy}
              className="px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 ring-1 ring-indigo-200 rounded disabled:opacity-40"
              data-testid="flashing-desc-admin-bulk-parse-btn"
              title="Scan all descriptions and auto-set diameter from any Ø / DN / mm pattern in the label"
            >{bulkBusy ? 'Scanning…' : 'Bulk-flag Ø'}</button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 text-xl leading-none"
              data-testid="flashing-desc-admin-close"
              title="Close"
            >×</button>
          </div>
        </div>

        <div className="px-4 py-3 space-y-4">
          {/* Add new row */}
          <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-2">Add description</div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10px] text-slate-500 mb-0.5">Label</label>
                <input
                  type="text"
                  value={draft.label}
                  onChange={e => setDraft({ ...draft, label: e.target.value })}
                  placeholder="e.g. Solar Backtray"
                  className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                  data-testid="flashing-desc-admin-new-label"
                />
              </div>
              <div className="w-40">
                <label className="block text-[10px] text-slate-500 mb-0.5">Group</label>
                <select
                  value={draft.group}
                  onChange={e => setDraft({ ...draft, group: e.target.value })}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
                  data-testid="flashing-desc-admin-new-group"
                >
                  {GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <button
                type="button"
                onClick={createItem}
                disabled={creating || !draft.label.trim()}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="flashing-desc-admin-new-add"
              >{creating ? 'Adding…' : '+ Add'}</button>
            </div>
          </div>

          {/* Existing rows grouped */}
          {loading ? (
            <div className="text-center text-sm text-slate-400 py-6">Loading…</div>
          ) : (
            grouped.map(g => (
              <div key={g.value}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{g.label}</div>
                {g.rows.length === 0 ? (
                  <div className="text-[11px] text-slate-400 italic px-2 py-1">— none —</div>
                ) : (
                  <ul className="divide-y divide-slate-100 ring-1 ring-slate-200 rounded bg-white">
                    {g.rows.map(item => {
                      const pending = edits[item._id] || {};
                      const dirty = (pending.label !== undefined && pending.label !== item.label)
                                 || (pending.group !== undefined && pending.group !== item.group);
                      return (
                        <li
                          key={item._id}
                          className="flex items-center gap-2 px-2 py-1.5"
                          data-testid={`flashing-desc-admin-row-${item.key}`}
                        >
                          <input
                            type="text"
                            value={pending.label !== undefined ? pending.label : (item.label || '')}
                            onChange={e => setEdits({ ...edits, [item._id]: { ...pending, label: e.target.value } })}
                            className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                            data-testid={`flashing-desc-admin-row-label-${item.key}`}
                          />
                          {item.diameter_mm ? (
                            <span
                              className="text-[10px] font-bold text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200 rounded px-1.5 py-0.5"
                              title={`Diameter currently set to ${item.diameter_mm} mm`}
                              data-testid={`flashing-desc-admin-row-dia-${item.key}`}
                            >Ø{item.diameter_mm}</span>
                          ) : (
                            <span className="text-[10px] text-slate-300 px-1" title="No diameter set">Ø—</span>
                          )}
                          <select
                            value={pending.group !== undefined ? pending.group : (item.group || 'simple')}
                            onChange={e => setEdits({ ...edits, [item._id]: { ...pending, group: e.target.value } })}
                            className="w-32 px-2 py-1 border border-slate-200 rounded text-xs bg-white"
                            data-testid={`flashing-desc-admin-row-group-${item.key}`}
                          >
                            {GROUPS.map(g => <option key={g.value} value={g.value}>{g.label.split(' ')[0]}</option>)}
                          </select>
                          {dirty && (
                            <button
                              type="button"
                              onClick={async () => {
                                await patchItem(item, {
                                  ...(pending.label !== undefined ? { label: pending.label } : {}),
                                  ...(pending.group !== undefined ? { group: pending.group } : {}),
                                });
                                const next = { ...edits };
                                delete next[item._id];
                                setEdits(next);
                              }}
                              className="px-2 py-1 text-[10px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded"
                              data-testid={`flashing-desc-admin-row-save-${item.key}`}
                            >Save</button>
                          )}
                          {item.is_seed && (
                            <span
                              className="text-[9px] uppercase font-semibold text-slate-400 px-1.5 py-0.5"
                              title="Seed entry — re-creates if you re-add via the form"
                            >seed</span>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteItem(item)}
                            className="text-rose-500 hover:bg-rose-50 px-1.5 py-1 rounded text-sm"
                            data-testid={`flashing-desc-admin-row-delete-${item.key}`}
                            title={item.is_seed ? 'Hide this seed (it stays in DB so any pins using it keep working)' : 'Delete'}
                          >🗑</button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-100 ring-1 ring-slate-300 rounded"
            data-testid="flashing-desc-admin-done"
          >Done</button>
        </div>

        {/* Iter232b — Bulk-Ø preview overlay */}
        {bulkPreview && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70"
            data-testid="flashing-desc-admin-bulk-preview-backdrop"
            onClick={() => !bulkBusy && setBulkPreview(null)}
          >
            <div
              className="bg-white rounded-lg shadow-2xl ring-1 ring-slate-300 w-[640px] max-w-[95vw] max-h-[85vh] flex flex-col"
              onClick={e => e.stopPropagation()}
              data-testid="flashing-desc-admin-bulk-preview-dialog"
            >
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-800">Bulk-flag Ø — preview</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Scanned <b>{bulkPreview.scanned}</b> active descriptions.
                  Will set Ø on <b className="text-indigo-700">{bulkPreview.would_change}</b> row{bulkPreview.would_change === 1 ? '' : 's'}.
                  Admin overrides are never touched.
                </p>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
                {(bulkPreview.proposed || []).filter(p => p.action === 'set').length === 0 ? (
                  <div className="text-[12px] text-slate-400 italic px-2 py-6 text-center">
                    No new diameters to set. Either every parseable description already has one, or no labels match the Ø / DN / mm patterns.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 ring-1 ring-slate-200 rounded">
                    {bulkPreview.proposed.filter(p => p.action === 'set').map(p => (
                      <li key={p.id} className="flex items-center gap-2 px-2 py-1.5 text-[12px]" data-testid={`bulk-preview-row-${p.id}`}>
                        <span className="flex-1 truncate text-slate-700">{p.label}</span>
                        <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200 rounded px-1.5 py-0.5">
                          Ø{p.will_set_to}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {(bulkPreview.proposed || []).some(p => p.action === 'skip-has-override') && (
                  <details className="text-[11px] text-slate-500 mt-2">
                    <summary className="cursor-pointer hover:text-slate-700">
                      {bulkPreview.proposed.filter(p => p.action === 'skip-has-override').length} skipped (already has admin override)
                    </summary>
                    <ul className="mt-1 pl-3 space-y-0.5">
                      {bulkPreview.proposed.filter(p => p.action === 'skip-has-override').map(p => (
                        <li key={p.id}>{p.label} — kept Ø{p.existing}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setBulkPreview(null)}
                  disabled={bulkBusy}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-100 ring-1 ring-slate-300 rounded disabled:opacity-40"
                  data-testid="bulk-preview-cancel"
                >Cancel</button>
                <button
                  type="button"
                  onClick={bulkApplyParseable}
                  disabled={bulkBusy || (bulkPreview.would_change || 0) === 0}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="bulk-preview-apply"
                >{bulkBusy ? 'Applying…' : `Apply (${bulkPreview.would_change || 0})`}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
